import { isAfter, isBefore, isValid } from 'date-fns';

/** Form data shape for the CID-CVN case details inputs. */
export type CidCvnCaseFormData = {
    /** The mileage value entered by the user. */
    mileage: string;

    /** The repair order number entered by the user. */
    repairOrderNumber: string;

    /** The repair order open date entered by the user (yyyy-MM-dd format). */
    repairOrderOpenDate: string;
};

/** Maximum length for the repair order number field. */
const MAX_RO_LENGTH = 10;

/**
 * Validate the mileage field. Must be present and greater than zero.
 *
 * @param value - the current field value
 * @returns an error message if invalid, undefined otherwise
 */
export function validateMileage(value: string): string | undefined {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return 'Mileage is required and must be greater than zero.';
    }

    const mileage = Number.parseInt(trimmedValue, 10);

    if (Number.isNaN(mileage) || mileage <= 0) {
        return 'Mileage must be greater than zero.';
    }

    return undefined;
}

/**
 * Validate the repair order number field.
 *
 * @param value - the current field value
 * @returns an error message if invalid, undefined otherwise
 */
export function validateRepairOrderNumber(value: string): string | undefined {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return 'Repair Order Number is required.';
    }

    if (trimmedValue.length > MAX_RO_LENGTH) {
        return `Repair Order Number must be ${MAX_RO_LENGTH} characters or fewer.`;
    }

    return undefined;
}

/**
 * Validate the repair order open date field. Must be present,
 * a valid date, not in the future, and not before 1753-01-01.
 *
 * @param value - the current field value
 * @returns an error message if invalid, undefined otherwise
 */
export function validateRepairOrderOpenDate(value: string): string | undefined {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return 'Repair Order Date is required.';
    }
    const date = new Date(trimmedValue);
    if (!isValid(date)) {
        return 'Repair Order Date is not a valid date.';
    }
    if (isAfter(date, new Date())) {
        return 'Repair Order Date cannot be a future date.';
    }
    if (isBefore(date, new Date('1753-01-01'))) {
        return 'Repair Order Date is not a valid date.';
    }
    return undefined;
}

/**
 * A mapping of form field keys to their corresponding validation functions.
 */
const fieldValidators: Record<keyof CidCvnCaseFormData, (value: string) => string | undefined> = {
    mileage: validateMileage,
    repairOrderNumber: validateRepairOrderNumber,
    repairOrderOpenDate: validateRepairOrderOpenDate,
};

/**
 * Validate all required fields specific to a CID/CVN Techline case.
 *
 * @param formData - the current form values
 * @returns field name → error message for invalid fields
 */
export function validateAllFields(formData: CidCvnCaseFormData): Partial<Record<keyof CidCvnCaseFormData, string>> {
    return {
        mileage: validateField('mileage', formData.mileage),
        repairOrderNumber: validateField('repairOrderNumber', formData.repairOrderNumber),
        repairOrderOpenDate: validateField('repairOrderOpenDate', formData.repairOrderOpenDate),
    };
}

/**
 * Validate a single required field specific to a CID/CVN Techline case.
 *
 * @param field - the form field key to validate
 * @param value - the field value
 * @returns an error message if invalid, undefined otherwise
 */
export function validateField(field: keyof CidCvnCaseFormData, value: string): string | undefined {
    return fieldValidators[field](value);
}
